<template>
  <b-modal :visible="show"
           size="lg"
           title="Text to audio"
           centered
           @close="$emit('close'); textToBeConverted = ''"
           @hide="$emit('close'); textToBeConverted = ''"
  >
    <div v-for="(textToConverted, index) in textToBeConvertedSsml"
         :key="index"
    >
      <b-row>
        <b-col md="9">
          <b-form-group label="Text to convert ">
            <b-form-textarea v-model="textToConverted.text"
                             placeholder="Text to convert"
                             rows="5"
            />
          </b-form-group>
        </b-col>
        <b-col md="3">
          <b-form-group v-if="!tryKokoro"
                        label="Engine"
          >
            <div class="d-flex align-items-center">
              <select v-model="textToConverted.engine"
                      class="form-control"
              >
                <option v-for="engine of voiceWithEngine"
                        :key="engine.text"
                        :value="engine.text"
                >
                  {{ engine.text }}
                </option>

              </select>
            </div>
          </b-form-group>
          <b-form-group label="Voices">
            <div class="d-flex align-items-center">
              <select v-model="textToConverted.voice"
                      class="form-control"
              >
                <option v-for="item of voiceOptions(textToConverted.engine)"
                        :key="item"
                        :value="item"
                >
                  {{ item }}
                </option>

              </select>
            </div>
            <div class="mt-1">
              <b-button
                v-show="index != 0"
                variant="outline-danger mr-1"
                size="sm"
                :disabled="isPolyProcessing"
                class="btn-icon rounded-circle"
                @click="removeText(index)"
              >
                <feather-icon size="14"
                              icon="MinusCircleIcon"
                />
              </b-button>
              <b-button variant="outline-primary"
                        size="sm"
                        class="btn-icon rounded-circle"
                        @click="previewPollyText(textToConverted)"
              >
                <feather-icon size="14"
                              icon="Volume2Icon"
                />
              </b-button>
            </div>
          </b-form-group>
        </b-col>
      </b-row>
    </div>
    <template #modal-footer>
      <div class="d-flex align-items-center justify-content-between w-100">
        <b-form-checkbox v-model="tryKokoro">
          Try Kokoro
        </b-form-checkbox>
        <div>
          <b-button
            variant="primary"
            class="btn-icon mr-2"
            @click="addMoreText"
          >
            <feather-icon icon="PlusCircleIcon" />
            Add More Text
          </b-button>
          <b-button variant="secondary"
                    @click="$emit('close')"
          >
            Cancel
          </b-button>
          <b-button variant="primary"
                    class="ml-2"
                    :disabled="(textToBeConvertedSsml[0].text =='') || isPolyProcessing"
                    @click="convertTextToPolySpeech"
          >
            <b-spinner v-if="isPolyProcessing"
                       small
            /> Convert
          </b-button>
        </div>
      </div>
    </template>
  </b-modal>
</template>
<script>
import {
  BModal, BFormGroup, BSpinner, BFormTextarea, BCol, BRow, BButton, BFormCheckbox,
} from 'bootstrap-vue'
import useJwt from '@/auth/jwt/useJwt'
import voiceWithEngine from '@/const/polly-voices'
import kokoroVoices from '@/const/kokoro-voices'

const defaultEngine = 'neural'
const defaultKokoroVoice = 'af_heart'

export default {
  components: {
    BModal,
    BFormGroup,
    BFormTextarea,
    BSpinner,
    BCol,
    BRow,
    BButton,
    BFormCheckbox,
  },

  props: {
    show: {
      type: Boolean,
      default: false,
    },
    previousUrl: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      isPolyProcessing: false,
      textToBeConverted: '',
      tryKokoro: false,
      voiceWithEngine,
      textToBeConvertedSsml: [
        {
          text: '',
          engine: defaultEngine,
          voice: '',
        },
      ],
    }
  },
  watch: {
    tryKokoro(val) {
      // Polly and Kokoro use entirely different voice ids, so a selection carried
      // across the toggle would be meaningless to whichever vendor now answers.
      this.textToBeConvertedSsml.forEach(item => {
        // eslint-disable-next-line no-param-reassign
        item.voice = val ? defaultKokoroVoice : ''
      })
    },
    show(val) {
      if (val && this.previousUrl) {
        useJwt.getAudioStore(this.previousUrl).then(response => {
          if (response.data) {
            this.textToBeConvertedSsml[0].text = response.data.data?.text || ''
            this.textToBeConvertedSsml[0].voice = response.data.data?.voice || ''
          }
        })
      }
    },
  },
  methods: {
    // Kokoro is opt in, so the vendor parameter only goes out when it is ticked.
    requestConfig() {
      return this.tryKokoro ? { params: { vendor: 'kokoro' } } : {}
    },
    // Kokoro exposes one flat voice list; Polly splits its voices per engine.
    voiceOptions(engine) {
      if (this.tryKokoro) {
        return kokoroVoices
      }

      return voiceWithEngine.find(i => i.text === engine)?.data || []
    },
    previewPollyText({ text, voice, engine }) {
      this.isPolyProcessing = true
      useJwt.convertTextToBase64([
        {
          text,
          voice,
          engine,
        },
      ], this.requestConfig()).then(response => {
        const base64AudioString = response.data.audio_file
        // eslint-disable-next-line no-undef
        const blob = this.base64StringToBlobUrl(base64AudioString)
        const objectUrl = URL.createObjectURL(blob.audio)
        const audio = new Audio(objectUrl)
        // Play the audio
        audio.play()
      }).finally(() => {
        this.isPolyProcessing = false
      })
    },
    addMoreText() {
      const newTexttoConvert = {
        text: '',
        engine: defaultEngine,
        voice: this.tryKokoro ? defaultKokoroVoice : '',
      }
      this.textToBeConvertedSsml.push(newTexttoConvert)
    },
    removeText(index) {
      this.textToBeConvertedSsml.splice(index, 1)
    },
    base64StringToBlobUrl(base64String) {
      // Convert the base64 string to a Uint8Array
      const arrayBuffer = Uint8Array.from(atob(base64String), c => c.charCodeAt(0))

      // Kokoro answers with WAV rather than the MP3 Polly returns, and "UklGR"
      // is the base64 for a "RIFF" header. The blob type carries through to the
      // data URI the parent stores, so it has to describe the real payload.
      const type = base64String.startsWith('UklGR') ? 'audio/wav' : 'audio/ogg'

      // Create a Blob from the Uint8Array
      const blob = new Blob([arrayBuffer], { type })

      // Create a blob URL
      const blobUrl = URL.createObjectURL(blob)

      return {
        audio: blob,
        url: blobUrl,
      }
    },
    convertTextToPolySpeech() {
      this.isPolyProcessing = true
      useJwt.convertTextToBase64(
        this.textToBeConvertedSsml,
        this.requestConfig(),
      ).then(response => {
        this.$emit('input', this.base64StringToBlobUrl(`${response.data.audio_file}`))
        this.$emit('close')
      }).finally(() => {
        this.isPolyProcessing = false
      })
    },
  },
}
</script>
